import React, { useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Box, Checkbox, TextField } from "@material-ui/core";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { Autocomplete, Input } from "components/Shared/mui-formik-inputs";
import { SupplierGroupSchema } from "constants/validation-schemas";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import { formatSnakeCaseToTitle, getPermissionTitle } from "util/helpers";

const useStyles = makeStyles({
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _icons: {
    color: "#ADADAD",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    margin: "0 16px 0 0px",
    fontSize: "35px",
  },
  _save: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#6F9CEB",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _close: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#525252",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: " #121212",
    marginTop: "32px",
    marginBottom: "24px",
  },
  _dflex: {
    display: "flex",
    alignItems: "center",
  },
});

const PermissionForm = ({ initialValues = {}, onSubmit, action, modules = [], permissions = [] }) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const parsedTitle = useMemo(() => {
    let title = initialValues?.title;
    if (typeof title === "string") {
      try {
        title = JSON.parse(title);
      } catch (e) {
        title = null;
      }
    }
    return title;
  }, [initialValues?.title]);

  const parsedDescription = useMemo(() => {
    let desc = initialValues?.description;
    if (typeof desc === "string") {
      try {
        desc = JSON.parse(desc);
      } catch (e) {
        desc = null;
      }
    }
    return desc;
  }, [initialValues?.description]);

  const parsedRules = useMemo(() => {
    let rules = initialValues?.rules;
    if (typeof rules === "string") {
      try {
        rules = JSON.parse(rules);
      } catch (e) {
        rules = null;
      }
    }
    return rules;
  }, [initialValues?.rules]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: SupplierGroupSchema,
    initialValues: {
      name: "",
      module: "",
      default: false,
      title_en: parsedTitle?.en || "",
      title_de: parsedTitle?.de || "",
      description_en: parsedDescription?.en || "",
      description_de: parsedDescription?.de || "",
      sort_order: initialValues?.sort_order || 0,
      depends_on: parsedRules?.depends_on || [],
      operator: parsedRules?.operator || "AND",
      exclusive_with: parsedRules?.exclusive_with || [],
      ...initialValues,
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        let snakeCaseName = values.name;
        const payload = {
          name: snakeCaseName,
          module: values.module,
          default: values.default,
          title: {
            en: values.title_en || "",
            de: values.title_de || ""
          },
          description: {
            en: values.description_en || "",
            de: values.description_de || ""
          },
          sort_order: Number(values.sort_order || 0),
          rules: (values.depends_on.length > 0 || values.exclusive_with?.length > 0) ? {
            depends_on: values.depends_on,
            operator: values.operator || "AND",
            exclusive_with: values.exclusive_with || []
          } : null
        };

        await onSubmit(payload);
      } catch (err) {
        console.error("Submit error:", err);
      } finally {
        setSubmitting(false);
      }
    }
  });

  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
  } = formik;
  let { handleBlur } = formik;

  if (!submitCount) {
    handleBlur = null;
  }

  const closeCustomerHandler = () => {
    action === "ADD"
      ? history.push(PATHS.admin.permissions)
      : history.push(PATHS.admin.permissionsDetail.replace(":id", id));
  };

  const moduleOptions = modules.map((mod) => ({ name: mod }));

  const dependeeOptions = useMemo(() => {
    if (!permissions || !values.module) return [];
    return permissions
      .filter((p) => p.module === values.module && p.id !== initialValues?.id)
      .map((p) => ({
        name: p.name,
        label: getPermissionTitle(p, i18n),
      }));
  }, [permissions, values.module, initialValues?.id, i18n, i18n?.language, t]);

  const selectedDependees = useMemo(() => {
    return dependeeOptions.filter((opt) => values.depends_on.includes(opt.name));
  }, [dependeeOptions, values.depends_on]);

  const selectedExclusive = useMemo(() => {
    return dependeeOptions.filter((opt) => values.exclusive_with?.includes(opt.name));
  }, [dependeeOptions, values.exclusive_with]);

  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);

  return (
    <Box height="100%">
      <Box display="flex" mb={4}>
        <Box flex={2}>
          <Typography className={classes._heading} variant="h4">
            {action === "ADD" ? t("New Permission") : t("Edit Permission")}
          </Typography>
        </Box>
        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={closeCustomerHandler}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>

      {/* Basic Data Section */}
      <Typography className={classes._subheading} variant="h5">
        {t("Basic Data")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Input
            label={t("System Name")}
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
            errors={errors}
            required
            disabled={action === "EDIT"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            onBlur={handleBlur}
            name="module"
            label={t("Module")}
            errors={errors}
            value={values.module}
            settings={{
              disableClearable: true,
              valueProp: "name",
              labelProp: "name",
              disabled: action === "EDIT"
            }}
            onChange={(selected) => {
              setFieldValue("module", selected?.name || "");
              setFieldValue("depends_on", []); // reset dependencies when module changes
            }}
            options={moduleOptions}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Box display="flex" flexDirection="row" alignItems="center" mt={1}>
            <Checkbox
              name="default"
              checked={Boolean(values.default)}
              onChange={(e) => {
                setFieldValue('default', e.target.checked);
              }}
              onBlur={handleBlur}
              style={{ color: "#6F9CEB" }}
            />
            <Typography component="span" className="font-size-16">
              {t("Default Value")}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Title, Description & Sorting Section */}
      <Typography className={classes._subheading} variant="h5">
        {t("Title, Description & Sorting")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Title (EN)")}
            name="title_en"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.title_en}
            errors={errors}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Title (DE)")}
            name="title_de"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.title_de}
            errors={errors}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Input
            label={t("Sort Order")}
            type="number"
            name="sort_order"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.sort_order}
            errors={errors}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Input
            label={t("Description (EN)")}
            name="description_en"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.description_en}
            errors={errors}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Input
            label={t("Description (DE)")}
            name="description_de"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.description_de}
            errors={errors}
          />
        </Grid>
      </Grid>

      {/* Dependency Rules Section */}
      <Typography className={classes._subheading} variant="h5">
        {t("Dependency Rules")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <MuiAutocomplete
            multiple
            id="depends-on-autocomplete"
            options={dependeeOptions}
            getOptionLabel={(option) => option?.label || option?.name || (typeof option === "string" ? option : "")}
            getOptionSelected={(option, val) => option?.name === (val?.name || val)}
            value={selectedDependees}
            onChange={(event, newValue) => {
              const selectedNames = newValue ? newValue.map((s) => s.name || s) : [];
              setFieldValue("depends_on", selectedNames);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Depends On Permissions")}
                variant="filled"
                error={!!errors.depends_on}
                helperText={errors.depends_on}
              />
            )}
            disabled={!values.module}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <MuiAutocomplete
            multiple
            id="exclusive-with-autocomplete"
            options={dependeeOptions}
            getOptionLabel={(option) => option?.label || option?.name || (typeof option === "string" ? option : "")}
            getOptionSelected={(option, val) => option?.name === (val?.name || val)}
            value={selectedExclusive}
            onChange={(event, newValue) => {
              const selectedNames = newValue ? newValue.map((s) => s.name || s) : [];
              setFieldValue("exclusive_with", selectedNames);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Exclusive With")}
                variant="filled"
                error={!!errors.exclusive_with}
                helperText={errors.exclusive_with}
              />
            )}
            disabled={!values.module}
          />
        </Grid>
        {values.depends_on.length > 0 && (
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Autocomplete
              onBlur={handleBlur}
              name="operator"
              label={t("Operator")}
              errors={errors}
              value={values.operator}
              settings={{
                disableClearable: true,
                valueProp: "value",
                labelProp: "label",
              }}
              onChange={(selected) => {
                setFieldValue("operator", selected?.value || "AND");
              }}
              options={[
                { value: "AND", label: t("AND") },
                { value: "OR", label: t("OR") },
              ]}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
export default PermissionForm;
